<template>
  <section class="watchlist-editor">
    <div v-if="isCurrentEditor" class="editor-owner-strip">
      You are the editor — please save when done.
    </div>

    <header class="editor-header">
      <div class="editor-actions">
        <div class="editor-presence">
          <span class="editor-avatar">{{ editorInitials }}</span>
          <span>{{ editorStatus }}</span>
        </div>

        <div class="editor-buttons">
          <button type="button" class="cancel-button" :disabled="saving" @click="cancelEditor">
            Cancel
          </button>

          <button
            type="button"
            class="submit-button"
            :disabled="saving || !isValid || editingBlocked || !isDirty"
            @click="submitChanges"
          >
            {{ submitButtonLabel }}
          </button>
        </div>
      </div>
    </header>

    <form class="editor-form" @submit.prevent="submitChanges">
      <fieldset class="edit-fields" :disabled="editingBlocked">
        <section class="form-card">
          <h4>Publication</h4>
          <label class="field-group">
            <span>Publication Status</span>
            <select v-model="publicationStatus">
              <option value="draft">Draft</option>
              <option value="inReview">In Review</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </section>

        <section class="form-card">
          <h4>Watchlist Information</h4>
          <div class="field-grid">
            <label class="field-group full-width">
              <span>Watchlist Name</span>
              <input v-model="name" type="text" autocomplete="off" />
            </label>
            <label class="field-group full-width">
              <span>Description</span>
              <textarea v-model="description" rows="5" />
            </label>

            <LitigationSearchMetadataInput
              v-model="tags"
              :original-values="originalTags"
              class="full-width"
              label="Tags"
              placeholder="Consumer services or consumer services, billing, subscriptions"
              hint="User-facing categories."
            />

            <LitigationSearchMetadataInput
              v-model="searchTerms"
              :original-values="originalSearchTerms"
              class="full-width"
              label="Search Terms"
              placeholder="Company name or company name, product, common complaint phrase"
              hint="Hidden search aliases."
            />

            <label class="field-group full-width">
              <span>Explainer Title</span>
              <input v-model="explainerTitle" type="text" autocomplete="off" />
            </label>
            <label class="field-group full-width">
              <span>Explainer Description</span>
              <textarea v-model="explainerDescription" rows="8" />
            </label>
          </div>
        </section>

        <WatchlistQuestionBuilder
          title="Watchlist Questions"
          description="Questions used to understand what happened and identify possible patterns."
          :questions="questions"
          @add="addQuestion"
          @remove="removeQuestion"
          @move-up="moveQuestionUp"
          @move-down="moveQuestionDown"
          @restore-base="restoreBaseQuestions"
        />

        <section class="form-card">
          <h4>Banner Image</h4>
          <img v-if="currentBannerUrl && !bannerRemovalRequested" :src="currentBannerUrl" class="banner-preview" alt="Current Watchlist banner" />

          <div class="banner-input-row">
            <input
              ref="bannerInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              @change="handleFileSelection"
            />
            <button
              v-if="selectedFileName || currentBannerUrl"
              type="button"
              class="remove-banner-button"
              :disabled="saving"
              @click="clearBannerSelection"
            >
              <span class="material-icons-outlined" aria-hidden="true">delete_outline</span>
            </button>
          </div>

          <span v-if="selectedFileName" class="banner-status">{{ selectedFileName }}</span>
          <span v-else-if="bannerRemovalRequested" class="banner-status removal-pending">
            Banner will be removed when changes are saved.
          </span>
        </section>
      </fieldset>

      <p v-if="editingBlocked" class="editing-message">
        {{ leaseError || `${editorName} is currently editing this Watchlist. This view is read-only.` }}
      </p>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </form>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import WatchlistQuestionBuilder from "@/components/watchlists/WatchlistQuestionBuilder.vue";
import LitigationSearchMetadataInput from "@/components/litigations/LitigationSearchMetadataInput.vue";
import { useWatchlistEditLease } from "@/composables/useWatchlistEditLease";
import { updateWatchlist } from "@/firebase/firestore";
import { deleteFile, uploadFile } from "@/firebase/storage";
import {
  cleanWatchlistQuestions,
  cloneWatchlistQuestions,
  createBaseWatchlistQuestions,
  createWatchlistQuestion
} from "@/models/watchlistQuestions";
import { normalizeSearchMetadata } from "@/models/searchMetadata";

export default {
  name: "WatchlistEditor",
  components: { WatchlistQuestionBuilder, LitigationSearchMetadataInput },
  props: {
    watchlist: { type: Object, required: true },
    adminId: { type: String, default: "" },
    adminName: { type: String, default: "Admin" }
  },
  emits: ["cancel", "saved"],

  setup(props, { emit }) {
    const watchlistId = computed(() =>
      props.watchlist.watchlistId ?? props.watchlist.id ?? ""
    );
    const name = ref(props.watchlist.name ?? "");
    const description = ref(props.watchlist.description ?? "");
    const originalTags = normalizeSearchMetadata(props.watchlist.tags);
    const originalSearchTerms = normalizeSearchMetadata(props.watchlist.searchTerms);
    const tags = ref([...originalTags]);
    const searchTerms = ref([...originalSearchTerms]);
    const explainerTitle = ref(props.watchlist.explainerTitle ?? "Watchlist");
    const explainerDescription = ref(props.watchlist.explainerDescription ?? "");
    const publicationStatus = ref(props.watchlist.publicationStatus ?? "inReview");
    const questions = ref(cloneWatchlistQuestions(props.watchlist.questions));
    const currentBannerUrl = ref(props.watchlist.bannerUrl ?? "");
    const selectedFile = ref(null);
    const selectedFileName = ref("");
    const bannerInput = ref(null);
    const bannerRemovalRequested = ref(false);
    const saving = ref(false);
    const errorMessage = ref("");
    const reorderRevision = ref(0);

    const {
      editingBlocked,
      editorInitials,
      editorName,
      editorStatus,
      isCurrentEditor,
      leaseError
    } = useWatchlistEditLease({
      watchlistId,
      adminId: computed(() => props.adminId),
      adminName: computed(() => props.adminName)
    });

    const cleanedQuestions = computed(() => cleanWatchlistQuestions(questions.value));
    const formSnapshot = computed(() => JSON.stringify({
      name: name.value,
      description: description.value,
      tags: tags.value,
      searchTerms: searchTerms.value,
      explainerTitle: explainerTitle.value,
      explainerDescription: explainerDescription.value,
      publicationStatus: publicationStatus.value,
      questions: questions.value,
      selectedFile: selectedFile.value ? {
        name: selectedFile.value.name,
        size: selectedFile.value.size,
        lastModified: selectedFile.value.lastModified
      } : null,
      bannerRemovalRequested: bannerRemovalRequested.value,
      reorderRevision: reorderRevision.value
    }));
    const lastSavedSnapshot = ref(formSnapshot.value);
    const isDirty = computed(() => formSnapshot.value !== lastSavedSnapshot.value);
    const hasBannerAfterSave = computed(() =>
      selectedFile.value !== null ||
      (currentBannerUrl.value !== "" && !bannerRemovalRequested.value)
    );
    const isValid = computed(() => {
      if (publicationStatus.value === "draft") { return true; }
      return name.value.trim() !== "" &&
        description.value.trim() !== "" &&
        explainerTitle.value.trim() !== "" &&
        explainerDescription.value.trim() !== "" &&
        cleanedQuestions.value.length > 0 &&
        hasBannerAfterSave.value;
    });
    const submitButtonLabel = computed(() => {
      const inReview = publicationStatus.value === "inReview";
      if (saving.value) { return inReview ? "Saving..." : "Publishing..."; }
      return inReview ? "Save Changes" : "Publish Changes";
    });

    const addQuestion = () => questions.value.push(createWatchlistQuestion());
    const removeQuestion = index => questions.value.splice(index, 1);
    const moveQuestion = (index, direction) => {
      const destination = index + direction;
      if (destination < 0 || destination >= questions.value.length) { return; }
      const [question] = questions.value.splice(index, 1);
      questions.value.splice(destination, 0, question);
      reorderRevision.value += 1;
    };
    const moveQuestionUp = index => moveQuestion(index, -1);
    const moveQuestionDown = index => moveQuestion(index, 1);
    const restoreBaseQuestions = () => {
      if (!window.confirm("Replace the current questions with the base Watchlist question set?")) {
        return;
      }
      questions.value = createBaseWatchlistQuestions();
    };

    const handleFileSelection = event => {
      const file = event.target.files?.[0] ?? null;
      selectedFile.value = file;
      selectedFileName.value = file?.name ?? "";
      if (file) { bannerRemovalRequested.value = false; }
    };
    const clearBannerSelection = () => {
      if (selectedFile.value) {
        selectedFile.value = null;
        selectedFileName.value = "";
        if (bannerInput.value) { bannerInput.value.value = ""; }
        return;
      }
      if (currentBannerUrl.value) { bannerRemovalRequested.value = true; }
    };

    const submitChanges = async () => {
      if (!isValid.value || !isDirty.value || saving.value || editingBlocked.value) {
        return;
      }

      saving.value = true;
      errorMessage.value = "";
      try {
        let bannerUrl = bannerRemovalRequested.value ? "" : currentBannerUrl.value;
        const oldBannerUrl = currentBannerUrl.value;

        if (selectedFile.value) {
          const extension = selectedFile.value.name.split(".").pop() ?? "png";
          bannerUrl = await uploadFile(
            selectedFile.value,
            `WatchFeed/${watchlistId.value}/banner.${extension}`
          );
        }

        const updated = await updateWatchlist(watchlistId.value, {
          name: name.value.trim(),
          description: description.value.trim(),
          tags: normalizeSearchMetadata(tags.value),
          searchTerms: normalizeSearchMetadata(searchTerms.value),
          explainerTitle: explainerTitle.value.trim(),
          explainerDescription: explainerDescription.value.trim(),
          bannerUrl,
          publicationStatus: publicationStatus.value,
          questions: cleanedQuestions.value
        });
        if (!updated) { throw new Error("Watchlist was not updated."); }

        if (bannerRemovalRequested.value && oldBannerUrl) {
          await deleteFile(oldBannerUrl);
        }
        currentBannerUrl.value = bannerUrl;
        selectedFile.value = null;
        selectedFileName.value = "";
        bannerRemovalRequested.value = false;
        if (bannerInput.value) { bannerInput.value.value = ""; }
        lastSavedSnapshot.value = formSnapshot.value;
        emit("saved", watchlistId.value);
      } catch (error) {
        console.error("❌ Unable to update Watchlist:", error);
        errorMessage.value = "Unable to save the Watchlist changes.";
      } finally {
        saving.value = false;
      }
    };

    const cancelEditor = () => {
      if (isDirty.value && !window.confirm("Discard your unsaved Watchlist changes?")) {
        return;
      }
      emit("cancel");
    };
    const warnBeforeUnload = event => {
      if (!isDirty.value || saving.value) { return; }
      event.preventDefault();
      event.returnValue = "";
    };
    onMounted(() => window.addEventListener("beforeunload", warnBeforeUnload));
    onBeforeUnmount(() => window.removeEventListener("beforeunload", warnBeforeUnload));

    return {
      name,
      description,
      tags,
      searchTerms,
      originalTags,
      originalSearchTerms,
      explainerTitle,
      explainerDescription,
      publicationStatus,
      questions,
      currentBannerUrl,
      selectedFileName,
      bannerInput,
      bannerRemovalRequested,
      saving,
      errorMessage,
      editingBlocked,
      editorInitials,
      editorName,
      editorStatus,
      isCurrentEditor,
      leaseError,
      isDirty,
      isValid,
      submitButtonLabel,
      addQuestion,
      removeQuestion,
      moveQuestionUp,
      moveQuestionDown,
      restoreBaseQuestions,
      handleFileSelection,
      clearBannerSelection,
      cancelEditor,
      submitChanges
    };
  }
};
</script>

<style scoped>
.watchlist-editor { display: flex; flex-direction: column; height: 100%; min-height: 0; background: #fbfcfb; }
.editor-owner-strip { padding: 8px 16px; background: #18ab69; color: #ffffff; font-size: 0.86rem; font-weight: 700; text-align: center; }
.editor-header { display: flex; align-items: center; padding: 18px 22px; border-bottom: 1px solid #e2e7e4; background: #ffffff; }
.editor-actions { display: flex; width: 100%; align-items: center; justify-content: space-between; gap: 24px; }
.editor-buttons, .editor-presence, .banner-input-row { display: flex; align-items: center; gap: 12px; }
.editor-presence { color: #66716b; font-size: 0.84rem; white-space: nowrap; }
.editor-avatar { display: grid; width: 34px; height: 34px; place-items: center; border-radius: 50%; background: #e9f5ef; color: #148254; font-weight: 800; }
.editor-form { min-height: 0; padding: 24px; overflow-y: auto; }
.edit-fields { display: flex; flex-direction: column; gap: 24px; min-width: 0; margin: 0; padding: 0; border: 0; }
.edit-fields:disabled { opacity: 0.72; }
.form-card { display: flex; flex-direction: column; gap: 18px; padding: 20px; border: 1px solid #e2e7e4; border-radius: 12px; background: #ffffff; }
.form-card h4 { margin: 0; color: #25322c; }
.field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.full-width { grid-column: 1 / -1; }
.field-group { display: flex; flex-direction: column; gap: 8px; color: #28342e; font-weight: 600; }
.field-group input, .field-group textarea, .field-group select { width: 100%; box-sizing: border-box; padding: 12px 13px; border: 1px solid #d7ded9; border-radius: 9px; background: #ffffff; font: inherit; }
.field-group textarea { resize: vertical; line-height: 1.5; }
.banner-input-row input { flex: 1; }
.remove-banner-button { display: grid; width: 42px; height: 42px; place-items: center; border: 1px solid #efc5c5; border-radius: 9px; background: #fff7f7; color: #b53b3b; cursor: pointer; }
.banner-preview { width: 100%; max-width: 760px; max-height: 280px; object-fit: cover; border-radius: 12px; }
.banner-status { color: #66716b; font-size: 0.86rem; }
.removal-pending { color: #a15c17; }
.editing-message, .error-message { margin: 18px 0 0; padding: 12px 14px; border-radius: 9px; }
.editing-message { border: 1px solid #ead39f; background: #fffaf0; color: #805b12; }
.error-message { border: 1px solid #efc5c5; background: #fff7f7; color: #a33636; }
.cancel-button, .submit-button { min-width: 145px; padding: 11px 18px; border: 0; border-radius: 9px; color: #ffffff; font: inherit; font-weight: 700; cursor: pointer; }
.cancel-button { background: #d64545; }
.submit-button { background: #18ab69; }
button:disabled { cursor: not-allowed; opacity: 0.55; }

@media (max-width: 900px) {
  .editor-actions { align-items: flex-start; flex-wrap: wrap; }
  .editor-buttons { margin-left: auto; }
  .field-grid { grid-template-columns: 1fr; }
  .full-width { grid-column: auto; }
}
</style>
